import React, { useState, useEffect, useContext } from 'react'
import { FreeAgencyContext } from '../../Context'
import { useLocation } from 'react-router-dom'
import {
  ContractYear,
  InnerConStyle,
  PlayerImgCon,
  SpinnerCon,
  TeamICCon,
} from './styles'
import { Box } from '../../Components/Layout'
import { FreeAgent } from '../../Api'
import { Spinner } from '../../Components/Icons'
import { colors } from '../../Components/consts'
import { TeamSelect } from '../../Components/Layout/Selectors/Team/TeamSelect'
import { PositionSelect } from '../../Components/Layout/Selectors/Position/PositionSelect'
import { CURRENT_YEAR } from '../../Config/League/league.config'
import {
  FREE_AGENCY_AD_INSERT_NUMBER,
  getTeamIc,
  shouldShowAd,
  getFullTeamName,
  capitalize,
} from '../../Utils'
import { TableFixedHead, TableOuterCon } from '../../Components/Tables/styles'
import { Desc } from '../../Components/Typography/Text.styles'
import { AdUnit } from '../../Components/Ads/AdUnit'
import { TableHeader } from '../../Components/Tables/TableHeader'
import { ResetBtn } from '../../Components/Buttons/styles'
const useQuery = () => {
  return new URLSearchParams(useLocation().search)
}

export const FreeAgencyTable: React.FC<React.PropsWithChildren<unknown>> = () => {
  let query = useQuery()
  const { players, loading } = useContext(FreeAgencyContext)!
  const [freeAgents, setFreeAgents] = useState<FreeAgent[]>(players)
  const [teamSelect, setTeamSelect] = useState<string>('All Teams')
  const [positionSelect, setPositionSelect] = useState<string>('All Positions')

  useEffect(() => {
    updateTeamFromParam()
  }, [])

  useEffect(() => {
    if (players.length > 0) {
      filterFreeAgents()
      return
    }
  }, [players])

  useEffect(() => {
    if (players.length > 0) {
      filterFreeAgents()
    }
  }, [teamSelect, positionSelect])

  const onChangeTeam = (tm: string) => {
    setTeamSelect(tm)
  }

  const updateTeamFromParam = () => {
    const teamQuery = query.get('team')
    if (teamQuery) {
      //Capitalize first letter of team
      const tm = capitalize(teamQuery)
      setTeamSelect(tm)
    }
    return
  }

  const onChangePos = (pos: string) => {
    setPositionSelect(pos)
  }

  const filterFreeAgents = () => {
    const filtered = players.filter(
      (t) =>
        (teamSelect === 'All Teams'
          ? t.player?.name
          : getFullTeamName(t.prevTeamId) === teamSelect ||
            getFullTeamName(t.newTeamId) === teamSelect) &&
        (positionSelect === 'All Positions'
          ? t.player?.position
          : t.player.position === positionSelect)
    )
    setFreeAgents(filtered)
  }

  const clearFilters = () => {
    setTeamSelect('All Teams')
    setPositionSelect('All Positions')
    setFreeAgents(players)
  }

  const renderTeamIc = (code: string) => {
    let fullName = getFullTeamName(code)
    if (!fullName) return
    return <img src={getTeamIc(fullName)} alt={fullName} />
  }

  const renderPlayerImg = (name: string, url: string) => {
    return <img src={url} alt={name} />
  }

  return (
    <Box noMargin padding={0}>
      {loading && (
        <SpinnerCon>
          <Spinner fill={colors.yellow} width={30} />
          Loading...
        </SpinnerCon>
      )}
      <InnerConStyle>
        <div className="selectorRow">
          <TeamSelect current={teamSelect} onChangeTeam={onChangeTeam} />
          <PositionSelect current={positionSelect} onChangePosition={onChangePos} />
          <ResetBtn onClick={clearFilters}>Reset</ResetBtn>
        </div>
        {!loading && freeAgents.length > 0 && (
          <TableOuterCon>
          <Desc justify="left">* Did not meet 30% threshold. See explainer.</Desc>
            <TableFixedHead wideFirstCol>
              <thead>
                <tr>
                  <TableHeader ariaLabel="Player">Player</TableHeader>
                  <TableHeader ariaLabel="Position">Pos</TableHeader>
                  <TableHeader ariaLabel="Age">Age</TableHeader>
                  <TableHeader ariaLabel="Experience">Exp</TableHeader>
                  <TableHeader ariaLabel={`${CURRENT_YEAR - 1} Team`}>{`${CURRENT_YEAR - 1} Team`}</TableHeader>
                  <TableHeader ariaLabel="New Team">New Team</TableHeader>
                  <TableHeader ariaLabel="Contract">Contract</TableHeader>
                </tr>
              </thead>
              <tbody>
                {freeAgents.map((row, idx) => (
                  <React.Fragment key={`${row.officialId}`}>
                    <tr>
                      <td>
                        <PlayerImgCon>
                            {renderPlayerImg(row.player.name, row.player.profileUrl)}
                            <p>{row.player?.name}</p>
                            {!row.thirtyPercentThresholdMet && <p>*</p>}
                        </PlayerImgCon>
                      </td>
                      <td>{row?.player?.position}</td>
                      <td>{row?.player?.age}</td>
                      <td>{row?.player?.experience}</td>
                      <td>
                        <TeamICCon>{renderTeamIc(row.prevTeamId)}</TeamICCon>
                      </td>
                      <td>
                        {row.newTeamId && (
                          <TeamICCon>{renderTeamIc(row.newTeamId)}</TeamICCon>
                        )}
                      </td>
                      {row.newContractStatus && (
                        <ContractYear color="green">
                          <p>
                            {row.newContractStatus
                              ? `through ${row.newContractStatus}`
                              : CURRENT_YEAR}
                          </p>
                        </ContractYear>
                      )}
                      {!row.newContractStatus &&
                        row.player?.status &&
                        !row.player?.status.includes('retired') && (
                          <ContractYear color="red">
                            <p>March {CURRENT_YEAR}</p>
                          </ContractYear>
                        )}
                      {!row.newContractStatus &&
                        row.player?.status &&
                        row.player?.status.includes('retired') && (
                          <ContractYear>
                            <p>Retired</p>
                          </ContractYear>
                        )}
                    </tr>
                    {shouldShowAd(idx, FREE_AGENCY_AD_INSERT_NUMBER) && (
                      <tr>
                        <td colSpan={7} align="center">
                          <AdUnit inContent={true} bannerType="bannerInContent" aria-label="Free Agency table ad" />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </TableFixedHead>
          </TableOuterCon>
        )}
      </InnerConStyle>
    </Box>
  )
}
